import { isTernaryOperator } from "@skilitics/judy";
import type { ValueExpression } from "@skilitics/nautilus-core-designer-data";
import { isValueTombstone } from "@skilitics/nautilus-core-designer-data";

import type { ConditionBuilder, ConditionBuilderContext } from "../../../types/index.ts";
import type { ResolveValueExpressionContext } from "../../../values/index.ts";
import { buildRuntimeExpression } from "../../../values/index.ts";
import { createConditionBuildable } from "../../conditions/index.ts";
import { ValueExpressionConditionEditor } from "./ValueExpressionConditionEditor.tsx";
import { ValueExpressionConditionSummary } from "./ValueExpressionConditionSummary.tsx";

export interface ValueExpressionConditionContext extends ConditionBuilderContext {
  config: ValueExpression;
}

/**
 * The free-standing expression: a comparison offered on every trigger, over any
 * value in scope, contributed by no node. Where a node's own condition builder
 * names a property of its result and knows the type it compares, this one is
 * handed a subject the author picked and has to ask what that subject is.
 *
 * Asking is the one thing it cannot do from a build context, which says nothing
 * about designer state and should not have to for every other builder's sake. So
 * the route from a subject to a build context arrives here instead, and the
 * builder is made with it rather than reaching for it.
 *
 * Beyond that it holds no comparison logic at all: the generic ExpressionForm
 * edits the expression, mounting whichever Comparison View the subject's value
 * subtype carries, and the shared assembly builds it.
 */
export const valueExpressionConditionBuildableCreate = (
  resolveValueExpressionContext: ResolveValueExpressionContext,
) => {
  const builder: ConditionBuilder<ValueExpressionConditionContext> = {
    typeId: "valueExpression",
    defaultCategory: "Utility",
    build: ({ config }) => {
      const context = config.subject && resolveValueExpressionContext(config.subject);
      return context && buildRuntimeExpression(config, context);
    },
    /**
     * What can be judged from the config alone: the parts are there, and the
     * subject is not a tombstone. A tombstoned subject names a value that has
     * gone, so `build` abstains over one and the condition is shown as
     * contributing nothing until the author picks a replacement.
     *
     * Whether the subject's subtype accepts the operator and operands is not
     * judged here — that needs the subtype's definition, which the form reaches
     * through hooks and this callback would have to read designer state for.
     */
    isValid: ({ subject, operator, value, value2 }) =>
      !!subject &&
      !isValueTombstone(subject) &&
      !!operator &&
      value !== undefined &&
      (!isTernaryOperator(operator) || value2 !== undefined),
    getInitialConfig: () => ({}),
    views: {
      summary: ValueExpressionConditionSummary,
      edit: ValueExpressionConditionEditor,
    },
  };

  return createConditionBuildable(builder, builder.typeId, { title: "Value Comparison" });
};
